import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  IconButton,
  Text,
  AttentionBox,
  Modal,
  ModalContent,
  ModalFooterButtons,
  ModalHeader,
} from '@vibe/core';
import { Add, Delete, Edit, Duplicate } from '@vibe/icons';
import {
  getCapacitySchemes,
  deleteCapacityScheme,
  duplicateCapacityScheme,
  type CapacityScheme,
  DAY_KEYS,
} from '../../api/capacity.api';
import { invalidateCapacityCache } from '../../hooks/useWorkload';

// ─── Props ──────────────────────────────────────────────────────────────────

interface CapacitySchemeListProps {
  onEdit: (scheme: CapacityScheme) => void;
  onCreateNew: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

const CapacitySchemeList: React.FC<CapacitySchemeListProps> = ({
  onEdit,
  onCreateNew,
}) => {
  const [schemes, setSchemes] = useState<CapacityScheme[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CapacityScheme | null>(null);

  const fetchSchemes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getCapacitySchemes();
      setSchemes(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load capacity schemes';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchemes();
  }, [fetchSchemes]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteCapacityScheme(deleteTarget.id);
      invalidateCapacityCache();
      setSchemes((prev) => prev.filter((s) => s.id !== deleteTarget.id));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete capacity scheme';
      setError(message);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const handleDuplicate = useCallback(async (scheme: CapacityScheme) => {
    try {
      const duplicated = await duplicateCapacityScheme(scheme.id);
      invalidateCapacityCache();
      setSchemes((prev) => [...prev, duplicated]);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to duplicate capacity scheme';
      setError(message);
    }
  }, []);

  /** Compute summary text for a scheme's schedule */
  const getScheduleSummary = (scheme: CapacityScheme): string => {
    const workingDays = DAY_KEYS.filter((k) => scheme.schedule[k] > 0);
    const totalHours = DAY_KEYS.reduce((sum, k) => sum + scheme.schedule[k], 0);
    const avg = workingDays.length > 0 ? totalHours / workingDays.length : 0;
    return `${avg.toFixed(1)}h/day \u00b7 ${workingDays.length} days/week`;
  };

  if (isLoading) {
    return (
      <div className="rp-capacity-scheme-list">
        <div className="rp-capacity-scheme-list__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-capacity-scheme-list">
      {/* Header */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-capacity-scheme-list__header"
      >
        <Heading type={Heading.types.H3}>Capacity Schemes</Heading>
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={onCreateNew}
        >
          Create New
        </Button>
      </Flex>

      {error && (
        <div className="rp-capacity-scheme-list__error">
          <AttentionBox
            type={AttentionBox.types.DANGER}
            text={error}
            onClose={() => setError(null)}
          />
        </div>
      )}

      {/* Scheme list */}
      {schemes.length === 0 && !error && (
        <div className="rp-capacity-scheme-list__empty">
          <Text>No capacity schemes yet. Create one to define work hours.</Text>
        </div>
      )}

      <div className="rp-capacity-scheme-list__items">
        {schemes.map((scheme) => (
          <div key={scheme.id} className="rp-capacity-scheme-list__item">
            <div className="rp-capacity-scheme-list__item-info">
              <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
                <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                  {scheme.name}
                </Text>
                {scheme.is_default && (
                  <span className="rp-capacity-scheme-list__badge">
                    Default
                  </span>
                )}
              </Flex>
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                {getScheduleSummary(scheme)}
              </Text>
            </div>
            <div className="rp-capacity-scheme-list__item-actions">
              <IconButton
                icon={Edit}
                size={IconButton.sizes.SMALL}
                onClick={() => onEdit(scheme)}
                ariaLabel={`Edit ${scheme.name}`}
              />
              <IconButton
                icon={Duplicate}
                size={IconButton.sizes.SMALL}
                onClick={() => handleDuplicate(scheme)}
                ariaLabel={`Duplicate ${scheme.name}`}
              />
              <IconButton
                icon={Delete}
                size={IconButton.sizes.SMALL}
                onClick={() => {
                  if (scheme.is_default) {
                    setError('Cannot delete the default scheme. Set another scheme as default first.');
                    return;
                  }
                  setDeleteTarget(scheme);
                }}
                ariaLabel={`Delete ${scheme.name}`}
                disabled={scheme.is_default}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Capacity Scheme"
      >
        <ModalHeader title="Delete Capacity Scheme" />
        <ModalContent>
          <Text style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
            All user and team assignments to this scheme will be removed.
            This action cannot be undone.
          </Text>
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onPrimaryButtonClick={handleDeleteConfirm}
          onSecondaryButtonClick={() => setDeleteTarget(null)}
        />
      </Modal>
    </div>
  );
};

export default CapacitySchemeList;
